import { DATA_FIELD_TYPE } from "Types/dataFieldTypes";
import { isModuleRegistered } from "SharedStores/util";
import { PROGRAM_TYPES, PROGRAM_NAMESPACE } from "SharedStores/Program/programStore";
import { PROJECT_TYPES, PROJECT_NAMESPACE } from "SharedStores/Project/projectStore";
import { FORM_NAMESPACE, FORM_TYPES } from "SharedStores/Form/formStore";
import { PROJECT_FORM_TYPES, ELEM_VIS_MUTATION } from "Forms/Components/ProjectForm/projectFormStore";
import { FormulaEvaluator } from "Formula/evaluator";
import { convertToFiniteNumber, IS_DEBUG_BUILD } from "Util/util";

const RELATIONAL_OPERATOR = {
    IsEqualTo: 1,
    IsNotEqualTo: 2,
    IsGreaterThan: 3,
    IsLessThan: 4,
    IsEmpty: 5,
    IsNotEmpty: 6,
    IsGreaterThanOrEqual: 7,
    IsLessThanOrEqual: 8
};

function _isRuleFulfilled(visibilityRule, getters) {
    if (!visibilityRule || !visibilityRule.DataFieldId) {
        return true;
    }
    const projectVal = getters[PROJECT_TYPES.getters.dataFieldValueFromId](visibilityRule.DataFieldId) || "";
    const valueIsEmpty = projectVal === "";

    const dataFieldDefinition = getters[PROGRAM_TYPES.getters.dataFieldDefinitionFromId](visibilityRule.DataFieldId);
    const typedProjectVal = _coerceValueToFieldType(projectVal, dataFieldDefinition.TypeId, {
        getters,
        getValueFromListItem: true
    });
    const operand = _coerceValueToFieldType(visibilityRule.RightOperand || "", dataFieldDefinition.TypeId);
    const operator = visibilityRule.Operator;

    switch (operator) {
        case RELATIONAL_OPERATOR.IsEqualTo:
            return typedProjectVal == operand;
        case RELATIONAL_OPERATOR.IsNotEqualTo:
            return !valueIsEmpty && typedProjectVal != operand;
        case RELATIONAL_OPERATOR.IsGreaterThan:
            return typedProjectVal > operand;
        case RELATIONAL_OPERATOR.IsGreaterThanOrEqual:
            return typedProjectVal >= operand;
        case RELATIONAL_OPERATOR.IsLessThan:
            return typedProjectVal < operand;
        case RELATIONAL_OPERATOR.IsLessThanOrEqual:
            return typedProjectVal <= operand;
        // don't coerce types for empty and not empty
        case RELATIONAL_OPERATOR.IsEmpty:
            return valueIsEmpty;
        case RELATIONAL_OPERATOR.IsNotEmpty:
            return !valueIsEmpty;
        default:
            console.error("Unrecognized visibility operator!");
    }
}

function _coerceValueToFieldType(value, fieldTypeId, { getters, getValueFromListItem } = {}) {
    switch (fieldTypeId) {
        case DATA_FIELD_TYPE.Integer: {
            return convertToFiniteNumber(value);
        }
        case DATA_FIELD_TYPE.Decimal: {
            return convertToFiniteNumber(value);
        }
        case DATA_FIELD_TYPE.DateTime: {
            return new Date(value).getTime();
        }
        case DATA_FIELD_TYPE.Boolean:
            if (typeof value === "boolean") {
                return value;
            }
            return value.toLowerCase() === "true";
        case DATA_FIELD_TYPE.Choice: {
            if (getters && getValueFromListItem) {
                const listItem = getters[PROGRAM_TYPES.getters.listItemFromId](value);
                if (listItem) {
                    const listItemValue = listItem.Value || "";
                    return listItemValue.toLowerCase();
                }
                return "";
            }
            if (typeof value === "string") {
                return value.toLowerCase();
            }
            return value;
        }
        default: {
            if (typeof value === "string") {
                return value.toLowerCase();
            }
            return value;
        }
    }
}
/**
 * just needs to get the values from the store that this depends on.
 * needs to be sync and more or less mirror _isElementVisible
 */
function _getValuesToWatch($app, visibilityRule, state, getters, $serviceControllers) {
    if (visibilityRule == null) {
        return;
    }
    if (visibilityRule.Formula) {
        const clone = JSON.parse(JSON.stringify(visibilityRule.Formula));
        const evaluator = new FormulaEvaluator(
            $app,
            clone,
            { rootState: state, rootGetters: getters },
            $serviceControllers
        );
        return evaluator.getReferencedValuesDictionary();
    } else if (visibilityRule.DataFieldId) {
        return _isElementVisibleWithSimpleRule(visibilityRule, getters);
    }
}

async function _isElementVisible($app, visibilityRule, state, getters, $serviceControllers) {
    if (visibilityRule == null) {
        return true;
    }
    if (visibilityRule.Formula) {
        const clone = JSON.parse(JSON.stringify(visibilityRule.Formula));
        const evaluator = new FormulaEvaluator(
            $app,
            clone,
            { rootState: state, rootGetters: getters },
            $serviceControllers
        );
        await evaluator.eval();
        return clone.result.Value === true;
    } else if (visibilityRule.DataFieldId) {
        return _isElementVisibleWithSimpleRule(visibilityRule, getters);
    }
    return true;
}
function _isElementVisibleWithSimpleRule(visibilityRule, getters) {
    const parentProjectElement = getters[PROJECT_FORM_TYPES.getters.projectElementFromDataFieldId](
        visibilityRule.DataFieldId
    );

    const isRuleFulfilled = _isRuleFulfilled(visibilityRule, getters);
    if (!isRuleFulfilled) {
        // rule is false, doesnt matter if parent is visibile or not
        return false;
    }
    //parent is not prensent, return true
    if (parentProjectElement) {
        //if parent is prensent, return false iff its not visibile and not a calc field
        const parentElement = getters[FORM_TYPES.getters.elementFromId](parentProjectElement.FormElementId);
        if (parentElement.ClassName !== "FormulaResultElement" && !parentProjectElement.IsVisible) {
            return false;
        }
    }
    return true;
}

export const visibilityManagerMixin = {
    data() {
        return {
            // Track the watchers we set up so that we can remove them
            // if we have to dynamically unhook the visibility mixin
            visibilityManager_unwatchHooks: []
        };
    },
    methods: {
        visibilityManager_init() {
            let start;
            if (IS_DEBUG_BUILD) {
                if (!isModuleRegistered(PROGRAM_NAMESPACE, this.$store.state)) {
                    throw Error("Program Module not registered.");
                }
                if (!isModuleRegistered(PROJECT_NAMESPACE, this.$store.state)) {
                    throw Error("Project Module not registered.");
                }
                if (!isModuleRegistered(FORM_NAMESPACE, this.$store.state)) {
                    throw Error("Form Module not registered.");
                }
                start = performance.now();
            }
            for (const publicId in this.$store.state[FORM_NAMESPACE].FormElements) {
                if (this.$store.state[FORM_NAMESPACE].FormElements.hasOwnProperty(publicId)) {
                    const element = this.$store.state[FORM_NAMESPACE].FormElements[publicId];
                    if (element.VisibilityRule) {
                        const unwatchFn = this.$store.watch(
                            (state, getters) => {
                                const valuesToWatch = _getValuesToWatch(
                                    this.$app,
                                    element.VisibilityRule,
                                    state,
                                    getters,
                                    this.$serviceControllers
                                );
                                return {
                                    valuesToWatch
                                };
                            },
                            () => this.visibilityManager_watcherCallback(element),
                            { deep: true }
                        );
                        this.visibilityManager_unwatchHooks.push(unwatchFn);
                    }
                }
            }
            if (IS_DEBUG_BUILD) {
                const end = performance.now();
                console.log(`Time elapsed in building vis dep index via mixin:${end - start}`);
            }
        },
        async visibilityManager_watcherCallback(element) {
            let isVisible = true;

            if (element.VisibilityRule) {
                isVisible = await _isElementVisible(
                    this.$app,
                    element.VisibilityRule,
                    this.$store.state,
                    this.$store.getters,
                    this.$serviceControllers
                );
            }

            const elementId = element.PublicId;
            // only trigger downstream events if vis value actually changed
            const projectElement = this.$store.getters[PROJECT_FORM_TYPES.getters.projectElementFromId](elementId);
            if (projectElement.IsVisible !== isVisible) {
                this.$store.commit(ELEM_VIS_MUTATION, { elementId, isVisible }, { root: true });
                if (isVisible) {
                    this.$store.dispatch(PROJECT_FORM_TYPES.actions.fillElementDefault, { elementId }, { root: true });
                } else {
                    this.$store.dispatch(PROJECT_FORM_TYPES.actions.clearElement, { elementId }, { root: true });
                }
            }
        },
        /**
         * Call to remove all existing watchers if we're dynamically loading
         * a new form without refreshing the page or destroying the projectForm
         */
        visibilityManager_unhook() {
            let start;
            if (IS_DEBUG_BUILD) {
                start = performance.now();
            }

            this.visibilityManager_unwatchHooks.forEach((unwatchHook) => unwatchHook());

            if (IS_DEBUG_BUILD) {
                const end = performance.now();
                console.log(`Time elapsed unhooking visibility mixin: ${end - start}`);
            }
        }
    }
};
